import { useId, type ImgHTMLAttributes } from "react";
import { Link } from "react-router-dom";

import { Icon, type IconName } from "../../components/icons";
import { PRECIO_TEXTO, PRIMER_TORNEO_TEXTO, WHATSAPP_AUTO_LIVE } from "./content";
import { usePageMeta } from "./hooks";
import { MiniVisual, type MiniVisualKind } from "./mockups/MiniVisuals";
import { PlayerHomeMock } from "./mockups/PlayerHomeMock";
import { publicAsset } from "./publicAsset";
import { SearchBar } from "./SearchBar";
import { TournamentCards } from "./TournamentCards";
import { HOME_GRID_SIZE, useTournamentDirectory } from "./useTournamentDirectory";
import "./HomePage.css";

/** React 18 todavía no reconoce la propiedad `fetchPriority` (llega en React
 * 19): se pasa en minúsculas para que el atributo llegue al HTML sin
 * advertencias en consola. */
const HIGH_FETCH_PRIORITY = { fetchpriority: "high" } as unknown as ImgHTMLAttributes<HTMLImageElement>;

interface OrganizerTile {
  icon: IconName;
  title: string;
  copy: string;
  visual: MiniVisualKind;
}

const ORGANIZER_TILES: OrganizerTile[] = [
  {
    icon: "wallet",
    title: "Cobro con Mercado Pago",
    copy: "Tus jugadores pagan en línea al inscribirse y el dinero llega directo a tu cuenta.",
    visual: "payment",
  },
  {
    icon: "bell",
    title: "Avisos a tus jugadores",
    copy: WHATSAPP_AUTO_LIVE
      ? "Recordatorio antes de cada partido y aviso si cambias el horario, por WhatsApp y en la app de su celular."
      : "Recordatorio antes de cada partido y aviso si cambias el horario, en la app de su celular. Por correo, si lo activas.",
    visual: "reminder",
  },
  {
    icon: "calendar",
    title: "Calendario de canchas",
    copy: "Acomoda todos los partidos del día en tus canchas en un clic, sin dos partidos en la misma cancha a la misma hora.",
    visual: "courts",
  },
  {
    icon: "bracket",
    title: "Formatos de torneo",
    copy: "Grupos con liguilla, eliminación directa, escalera, dobles y por equipos. Tenis, pádel y pickleball.",
    visual: "formats",
  },
];

const ORGANIZER_LEAD = WHATSAPP_AUTO_LIVE
  ? "Inscripción con pago en línea, cuadros automáticos, calendario de canchas y avisos a tus jugadores por WhatsApp y en la app de su celular."
  : "Inscripción con pago en línea, cuadros automáticos, calendario de canchas y avisos a la app de cada jugador.";

const PLAYER_ROWS: { icon: IconName; title: string; copy: string }[] = [
  { icon: "ticket", title: "Inscríbete y paga desde el celular", copy: "Con el enlace de tu torneo, en minutos." },
  {
    icon: "calendar",
    title: "Tu próximo partido a la mano",
    copy: WHATSAPP_AUTO_LIVE
      ? "Hora, cancha y rival, con recordatorio por WhatsApp antes de jugar."
      : "Hora, cancha y rival, con recordatorio antes de jugar.",
  },
  { icon: "trophy", title: "Cuadro, resultados y ranking", copy: "Sigue tu torneo y tu historial de partidos." },
];

/** Inicio de torneos123.com: portada → buscador y próximos torneos → franja
 * de organizadores → franja de jugadores → franja final. */
export function HomePage() {
  usePageMeta("home");
  const { tournaments, status } = useTournamentDirectory();
  return (
    <main className="t123-home">
      <HomeHero />

      <section className="t123-home__upcoming" id="torneos" aria-labelledby="t123-home-upcoming-title">
        <div className="t123-shell">
          <SearchBar className="t123-home__search" />
          <div className="t123-home__head">
            <h2 id="t123-home-upcoming-title">Próximos torneos</h2>
            <Link className="t123-link-arrow t123-home__see-all" to="/torneos">
              Ver todos
            </Link>
          </div>
          <TournamentCards tournaments={tournaments} status={status} limit={HOME_GRID_SIZE} withPromo />
        </div>
      </section>

      <OrganizerBand />
      <PlayerBand />

      <CtaBand
        title="¿Organizas torneos?"
        copy={`${PRIMER_TORNEO_TEXTO}. Después, ${PRECIO_TEXTO}.`}
        primary={{ to: "/organizador#demo", label: "Pedir demo" }}
        secondary={{ to: "/torneos", label: "Ver torneos" }}
      />
    </main>
  );
}

function HomeHero() {
  const small = publicAsset("torneos123/hero-tenista-900.webp");
  const large = publicAsset("torneos123/hero-tenista-1600.webp");
  return (
    <section className="t123-hero t123-home__hero" aria-labelledby="t123-home-title">
      <div className="t123-hero__dots" aria-hidden="true" />
      <div className="t123-shell t123-hero__grid">
        <div className="t123-hero__copy-block">
          <h1 id="t123-home-title">
            Vive tu deporte.
            <br />
            Juega, compite
            <br />y <span>sube de nivel.</span>
          </h1>
          <p>
            Encuentra torneos de tenis, pádel y pickleball en México, inscríbete desde tu celular y sigue tus partidos,
            resultados y ranking en un solo lugar.
          </p>
          <div className="t123-hero__actions">
            <Link className="t123-btn t123-btn--primary t123-btn--arrow" to="/torneos">
              Ver torneos
            </Link>
            <Link className="t123-btn t123-btn--outline t123-btn--arrow" to="/organizador">
              Organiza un torneo
            </Link>
          </div>
        </div>

        <div className="t123-hero__art">
          <img
            src={large}
            srcSet={`${small} 900w, ${large} 1600w`}
            sizes="(max-width: 860px) 100vw, 60vw"
            width={1600}
            height={884}
            decoding="async"
            {...HIGH_FETCH_PRIORITY}
            alt="Tenista preparando un revés"
          />
        </div>
      </div>
    </section>
  );
}

function OrganizerBand() {
  return (
    <section
      className="t123-section t123-section--green t123-home__org"
      id="organizadores"
      aria-labelledby="t123-home-org-title"
    >
      <div className="t123-shell t123-home__org-grid">
        <div className="t123-home__org-copy">
          <span className="t123-kicker">Para organizadores</span>
          <h2 className="t123-h2" id="t123-home-org-title">
            ¿Organizas torneos? <span>Llénalos, cóbralos y opéralos sin Excel.</span>
          </h2>
          <p className="t123-lead">{ORGANIZER_LEAD}</p>
          <div className="t123-home__price-row">
            <span className="t123-home__price">{PRECIO_TEXTO}</span>
            <span className="t123-chip t123-chip--dark">
              <Icon name="check" />
              {PRIMER_TORNEO_TEXTO}
            </span>
          </div>
          <div className="t123-hero__actions t123-home__actions">
            <Link className="t123-btn t123-btn--primary t123-btn--arrow" to="/organizador#demo">
              Pedir demo
            </Link>
            <Link className="t123-btn t123-btn--outline t123-btn--arrow" to="/organizador#recorrido">
              Ver cómo funciona
            </Link>
          </div>
        </div>

        <div className="t123-home__tiles-wrap">
          <ul className="t123-home__tiles">
            {ORGANIZER_TILES.map((tile) => (
              <li key={tile.title} className="t123-home__tile">
                <div className="t123-home__tile-head">
                  <span className="t123-tile-icon" aria-hidden="true">
                    <Icon name={tile.icon} />
                  </span>
                  <h3>{tile.title}</h3>
                </div>
                <p>{tile.copy}</p>
                <div className="t123-home__tile-visual">
                  <MiniVisual kind={tile.visual} />
                </div>
              </li>
            ))}
          </ul>
          <p className="t123-home__sample-note">Datos de ejemplo</p>
        </div>
      </div>
    </section>
  );
}

function PlayerBand() {
  return (
    <section className="t123-section t123-home__players" id="jugadores" aria-labelledby="t123-home-players-title">
      <div className="t123-shell t123-home__players-grid">
        <div className="t123-home__players-copy">
          <span className="t123-kicker">Para jugadores</span>
          <h2 className="t123-h2" id="t123-home-players-title">
            Tu torneo, en tu celular.
          </h2>
          <ul className="t123-home__rows">
            {PLAYER_ROWS.map((row) => (
              <li key={row.title}>
                <span className="t123-tile-icon" aria-hidden="true">
                  <Icon name={row.icon} />
                </span>
                <div>
                  <h3>{row.title}</h3>
                  <p>{row.copy}</p>
                </div>
              </li>
            ))}
          </ul>
          <div className="t123-hero__actions t123-home__actions">
            <Link className="t123-btn t123-btn--primary t123-btn--arrow" to="/torneos">
              Ver torneos
            </Link>
            <Link className="t123-btn t123-btn--outline t123-btn--arrow" to="/jugador">
              Más para jugadores
            </Link>
          </div>
        </div>
        <div className="t123-home__players-mock">
          <PlayerHomeMock />
        </div>
      </div>
    </section>
  );
}

interface CtaLink {
  to: string;
  label: string;
}

/** Franja final azul marino con la pelota (inicio, /torneos y /jugador). */
export function CtaBand({
  title,
  copy,
  primary,
  secondary,
}: {
  title: string;
  copy: string;
  primary: CtaLink;
  secondary?: CtaLink;
}) {
  const titleId = useId();
  return (
    <section className="t123-footer-cta t123-ctaband" aria-labelledby={titleId}>
      <div className="t123-shell t123-footer-cta__box t123-ctaband__box">
        <div className="t123-footer-ball" aria-hidden="true" />
        <div className="t123-ctaband__copy">
          <h2 id={titleId}>{title}</h2>
          <p>{copy}</p>
        </div>
        <div className="t123-ctaband__actions">
          <Link className="t123-btn t123-btn--primary t123-btn--arrow" to={primary.to}>
            {primary.label}
          </Link>
          {secondary ? (
            <Link className="t123-btn t123-btn--outline t123-btn--arrow t123-ctaband__outline" to={secondary.to}>
              {secondary.label}
            </Link>
          ) : null}
        </div>
      </div>
    </section>
  );
}
